import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { PrimaryButton } from '../../components/PrimaryButton';
import { FloatingLabelInput } from '../../components/FloatingLabelInput';
import { OtpVerify } from '../../components/OtpVerify';
import {
  changeOwnPassword,
  changeOwnEmailSendOtp,
  changeOwnEmailVerify,
} from '../../../lib/professional-api';
import { apiErrorMessage } from '../../../lib/api';
import { patchUser } from '../../../lib/auth';
import { RequirePro } from '../../../lib/use-auth';
import {
  passwordStrengthError,
  STRONG_PASSWORD_MESSAGE,
} from '../../../lib/validators';
import { toast } from '../../../lib/toast';

type Mode = 'password' | 'email';
type EmailStep = 'form' | 'otp' | 'done';

export default function ProChangePasswordLoginPage() {
  return (
    <RequirePro>
      <ProChangePasswordInner />
    </RequirePro>
  );
}

function ProChangePasswordInner() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('password');

  // ---------- Password flow ----------
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdErrors, setPwdErrors] = useState<{
    current?: string;
    next?: string;
    confirm?: string;
  }>({});
  const [pwdSaving, setPwdSaving] = useState(false);

  const validatePassword = () => {
    const next: typeof pwdErrors = {};
    if (!currentPassword) next.current = 'Current password is required.';
    if (!newPassword) next.next = 'Enter a new password.';
    else if (passwordStrengthError(newPassword))
      next.next = STRONG_PASSWORD_MESSAGE;
    if (confirmPassword !== newPassword)
      next.confirm = 'Passwords do not match.';
    setPwdErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSavePassword = async () => {
    if (!validatePassword()) return;
    setPwdSaving(true);
    try {
      await changeOwnPassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password updated.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      navigate('/professional/account-settings');
    } catch (err) {
      const msg = apiErrorMessage(err, 'Could not update password.');
      if (/old|current|incorrect/i.test(msg)) {
        setPwdErrors((p) => ({ ...p, current: msg }));
      }
      toast.error(msg);
    } finally {
      setPwdSaving(false);
    }
  };

  // ---------- Email flow (two-step OTP) ----------
  const [emailStep, setEmailStep] = useState<EmailStep>('form');
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailReference, setEmailReference] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [emailErrors, setEmailErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [otpError, setOtpError] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailVerifying, setEmailVerifying] = useState(false);

  const validEmail = (v: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const handleSendEmailOtp = async () => {
    const next: typeof emailErrors = {};
    if (!validEmail(newEmail)) next.email = 'Enter a valid email address.';
    if (!emailPassword) next.password = 'Current password is required.';
    setEmailErrors(next);
    if (Object.keys(next).length > 0) return;
    setEmailSending(true);
    try {
      const res = await changeOwnEmailSendOtp({
        new_email: newEmail.trim().toLowerCase(),
        current_password: emailPassword,
      });
      setEmailReference(res.reference_number);
      setOtp('');
      setOtpError(false);
      setEmailStep('otp');
      toast.success('We sent a code to your new email.');
    } catch (err) {
      const msg = apiErrorMessage(err, 'Could not send code.');
      if (/password|incorrect|old|current/i.test(msg)) {
        setEmailErrors((p) => ({ ...p, password: msg }));
      } else if (/email|in use|exist|register/i.test(msg)) {
        setEmailErrors((p) => ({ ...p, email: msg }));
      }
      toast.error(msg);
    } finally {
      setEmailSending(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (!emailReference || otp.length !== 6) return;
    setEmailVerifying(true);
    setOtpError(false);
    try {
      await changeOwnEmailVerify({
        reference_number: emailReference,
        new_email: newEmail.trim().toLowerCase(),
        otp,
      });
      // Patch the cached pro so the header etc. show the new email
      // immediately — the JWT is still valid (we identify by id).
      patchUser({ email: newEmail.trim().toLowerCase() });
      setEmailStep('done');
      toast.success('Email updated.');
    } catch (err) {
      setOtpError(true);
      toast.error(apiErrorMessage(err, 'Invalid or expired code.'));
    } finally {
      setEmailVerifying(false);
    }
  };

  const handleResendEmailOtp = () => {
    handleSendEmailOtp();
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HeaderPro />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-6 md:py-12">
        {/* Back and Title */}
        <div className="flex items-start gap-4 mb-10">
          <Link
            to="/professional/account-settings"
            aria-label="Back to account settings"
            className="mt-1 w-[40px] h-[40px] bg-[#3a3335]/[0.04] rounded-[10px] flex items-center justify-center hover:bg-[#3a3335]/[0.08] transition-colors shrink-0"
          >
            <span className="material-icons text-[#908e8e] text-[18px]">
              chevron_left
            </span>
          </Link>
          <div>
            <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px] mb-1">
              Login details
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              Update the password or email you use to sign in
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="inline-flex bg-[#f4f4f4] rounded-[12px] p-1 mb-8">
          <TabButton
            active={mode === 'password'}
            onClick={() => setMode('password')}
          >
            Change password
          </TabButton>
          <TabButton
            active={mode === 'email'}
            onClick={() => setMode('email')}
          >
            Change email
          </TabButton>
        </div>

        <div className="flex flex-col lg:flex-row justify-between gap-12">
          <div className="flex-1 max-w-[556px]">
            {mode === 'password' ? (
              <>
                <h2 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-[16px] mb-6">
                  Change password
                </h2>
                <div className="space-y-6">
                  <FloatingLabelInput
                    label="Current password"
                    type="password"
                    value={currentPassword}
                    onChange={(v) => {
                      setCurrentPassword(v);
                      if (pwdErrors.current)
                        setPwdErrors((p) => ({ ...p, current: undefined }));
                    }}
                    icon="lock"
                    containerHeight="h-[72px]"
                    error={!!pwdErrors.current}
                    errorMessage={pwdErrors.current}
                  />
                  <FloatingLabelInput
                    label="New password"
                    type="password"
                    value={newPassword}
                    onChange={(v) => {
                      setNewPassword(v);
                      if (pwdErrors.next)
                        setPwdErrors((p) => ({ ...p, next: undefined }));
                    }}
                    icon="check_circle"
                    className="[&_span]:text-[#c6d8d3]"
                    containerHeight="h-[72px]"
                    error={!!pwdErrors.next}
                    errorMessage={pwdErrors.next}
                  />
                  <FloatingLabelInput
                    label="Confirm new password"
                    type="password"
                    value={confirmPassword}
                    onChange={(v) => {
                      setConfirmPassword(v);
                      if (pwdErrors.confirm)
                        setPwdErrors((p) => ({ ...p, confirm: undefined }));
                    }}
                    icon="check_circle"
                    containerHeight="h-[72px]"
                    error={!!pwdErrors.confirm}
                    errorMessage={pwdErrors.confirm}
                  />
                </div>
                <div className="mt-12 flex items-center gap-4">
                  <PrimaryButton
                    className="w-auto px-6 h-[50px] text-[14px] whitespace-nowrap"
                    onClick={handleSavePassword}
                    disabled={pwdSaving}
                  >
                    {pwdSaving ? 'Updating…' : 'Update password'}
                  </PrimaryButton>
                  <button
                    type="button"
                    onClick={() => navigate('/professional/account-settings')}
                    className="px-8 h-[50px] rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[#908e8e] text-[14px] bg-[#3a3335]/[0.04] hover:bg-[#3a3335]/[0.08] transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : emailStep === 'form' ? (
              <>
                <h2 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-[16px] mb-1">
                  Change email
                </h2>
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] mb-6">
                  We'll send a code to your new email to confirm it's
                  really yours before we swap it.
                </p>
                <div className="space-y-6">
                  <FloatingLabelInput
                    label="New email address"
                    type="email"
                    value={newEmail}
                    onChange={(v) => {
                      setNewEmail(v);
                      if (emailErrors.email)
                        setEmailErrors((p) => ({ ...p, email: undefined }));
                    }}
                    icon="mail"
                    containerHeight="h-[72px]"
                    error={!!emailErrors.email}
                    errorMessage={emailErrors.email}
                  />
                  <FloatingLabelInput
                    label="Current password"
                    type="password"
                    value={emailPassword}
                    onChange={(v) => {
                      setEmailPassword(v);
                      if (emailErrors.password)
                        setEmailErrors((p) => ({ ...p, password: undefined }));
                    }}
                    icon="lock"
                    containerHeight="h-[72px]"
                    error={!!emailErrors.password}
                    errorMessage={emailErrors.password}
                  />
                </div>
                <div className="mt-12 flex items-center gap-4">
                  <PrimaryButton
                    className="w-auto px-6 h-[50px] text-[14px] whitespace-nowrap"
                    onClick={handleSendEmailOtp}
                    disabled={emailSending}
                  >
                    {emailSending ? 'Sending…' : 'Send code'}
                  </PrimaryButton>
                  <button
                    type="button"
                    onClick={() => navigate('/professional/account-settings')}
                    className="px-8 h-[50px] rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[#908e8e] text-[14px] bg-[#3a3335]/[0.04] hover:bg-[#3a3335]/[0.08] transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : emailStep === 'otp' ? (
              <>
                <h2 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-[16px] mb-1">
                  Enter the 6-digit code
                </h2>
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] mb-6">
                  We emailed a code to{' '}
                  <span className="font-bold text-[#3a3335]">
                    {newEmail.trim().toLowerCase()}
                  </span>
                  . It expires in 10 minutes.
                </p>
                <div className="flex flex-col gap-6">
                  <OtpVerify
                    value={otp}
                    onChange={(v) => {
                      setOtp(v);
                      if (otpError) setOtpError(false);
                    }}
                    onVerify={handleVerifyEmailOtp}
                    onResend={handleResendEmailOtp}
                    submitting={emailVerifying}
                    error={otpError}
                    resendSeconds={60}
                  />
                </div>
                <div className="mt-8 flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setEmailStep('form')}
                    className="px-8 h-[44px] rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[#908e8e] text-[13px] bg-[#3a3335]/[0.04] hover:bg-[#3a3335]/[0.08] transition-all"
                  >
                    Use a different email
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-[16px] mb-1">
                  Email updated
                </h2>
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] mb-6">
                  Your login email is now{' '}
                  <span className="font-bold text-[#3a3335]">
                    {newEmail.trim().toLowerCase()}
                  </span>
                  . Use it next time you sign in.
                </p>
                <PrimaryButton
                  className="w-auto px-6 h-[50px] text-[14px] whitespace-nowrap"
                  onClick={() => navigate('/professional/account-settings')}
                >
                  Back to account
                </PrimaryButton>
              </>
            )}
          </div>

          {/* Right Info Card */}
          <div className="w-full lg:w-[361px] shrink-0">
            <div className="bg-white rounded-[6px] shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] p-8">
              <div className="w-[48px] h-[48px] rounded-[8px] bg-[#3a3335]/[0.04] flex items-center justify-center mb-6">
                <span className="material-icons text-[#908e8e] text-[24px]">
                  {mode === 'password' ? 'lock' : 'mail'}
                </span>
              </div>
              <h3 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[24px] mb-3">
                {mode === 'password'
                  ? 'Keep your account safe'
                  : 'Verify before we swap'}
              </h3>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px] tracking-[-0.1px]">
                {mode === 'password'
                  ? "Use at least 8 characters with an uppercase letter, a lowercase letter, a number, and a special character. We'll keep you signed in on this device — other sessions stay live until they expire or you sign out from them on the Security page."
                  : "We send a 6-digit code to the new email to prove you own it. The change only goes through once you enter that code — your current email stays active until then."}
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-5 py-2 rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[14px] transition-all ${
        active
          ? 'bg-white text-[#8e2a2f] shadow-[0px_2px_6px_0px_rgba(153,155,168,0.2)]'
          : 'text-[#908e8e] hover:text-[#3a3335]'
      }`}
    >
      {children}
    </button>
  );
}
